import {
    Modal,
    TextInput,
    Stack,
    SimpleGrid,
    Text,
    Group, Center,
    Box,
    Skeleton, CloseButton, ActionIcon, Tooltip
} from '@mantine/core'
import { IconSearch, IconRefresh } from '@tabler/icons-react'
import { useState, useEffect } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useDebouncedValue, useMediaQuery } from '@mantine/hooks'
import { gamesApi } from '../services/api'
import { GameCard } from './GameCard'
import type { GamePlatform } from '../types'
import { useI18n } from '@/hooks/useI18n'

interface LowestPrice {
    amount?: number
    currency?: string
    priceDrop?: number
    percentageChange?: number
    store?: {
        id: string
        name: string
    } | null
}

interface SearchOverlayProps {
    opened: boolean
    onClose: () => void
}

export const SearchOverlay: React.FC<SearchOverlayProps> = ({ opened, onClose }) => {
    const { t, country } = useI18n()
    const queryClient = useQueryClient()
    const [searchQuery, setSearchQuery] = useState('')
    const [debouncedQuery] = useDebouncedValue(searchQuery, 300)

    // Detect mobile screens for full-screen modal
    const isMobile = useMediaQuery('(max-width: 768px)')

    const { data: searchResults, isLoading: isSearching, error, refetch } = useQuery({
        queryKey: ['search', debouncedQuery, country],
        queryFn: () => gamesApi.searchGames(debouncedQuery, country),
        enabled: debouncedQuery.length >= 2,
        select: (response) => response.data.data,
    })

    // Clear search when modal closes
    useEffect(() => {
        if (!opened) {
            setSearchQuery('')
        }
    }, [opened])

    // Invalidate search cache and force fresh results when modal opens
    useEffect(() => {
        if (opened) {
            queryClient.invalidateQueries({ queryKey: ['search'] })
            if (debouncedQuery.length >= 2) {
                refetch() // Refetch current search if there's a term
            }
        }
    }, [opened, queryClient, debouncedQuery, refetch])

    const handleCardClick = () => {
        onClose()
    }

    return (
        <Modal
            opened={opened}
            onClose={onClose}
            size={isMobile ? "100%" : "90%"}
            fullScreen={isMobile}
            withCloseButton={false}
            styles={{
                root: {
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    zIndex: 1000,
                },
                inner: {
                    display: 'flex',
                    alignItems: isMobile ? 'stretch' : 'flex-start',
                    justifyContent: 'center',
                    paddingTop: isMobile ? 0 : '10vh',
                    paddingBottom: isMobile ? 0 : '2rem',
                    height: isMobile ? '100vh' : 'auto',
                },
                content: {
                    maxWidth: isMobile ? '100%' : '1200px',
                    width: '100%',
                    maxHeight: isMobile ? '100vh' : '80vh',
                    height: isMobile ? '100vh' : 'auto',
                    display: 'flex',
                    flexDirection: 'column',
                    borderRadius: isMobile ? 0 : '12px',
                    boxShadow: isMobile ? 'none' : '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
                },
                body: {
                    padding: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    flex: 1,
                    minHeight: 0, // Important for flex scrolling
                },
            }}
            overlayProps={{
                backgroundOpacity: isMobile ? 0 : 0.55,
                blur: isMobile ? 0 : 3,
            }}
        >
            <Stack gap={0} style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                {/* Search Header */}
                <Box p={isMobile ? "lg" : "md"} style={{
                    borderBottom: '1px solid var(--mantine-color-gray-3)',
                    flexShrink: 0,
                    backgroundColor: 'var(--mantine-color-body)'
                }}>
                    <Group gap="md" align="center">
                        <IconSearch size={isMobile ? 24 : 20} color="var(--mantine-color-gray-6)" />
                        <TextInput
                            placeholder={t('common.searchPlaceholder')}
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            size={isMobile ? "lg" : "md"}
                            variant="unstyled"
                            style={{ flex: 1 }}
                            autoFocus
                        />
                        {debouncedQuery.length >= 2 && (
                            <Tooltip label="Refresh search results">
                                <ActionIcon
                                    variant="subtle"
                                    onClick={() => {
                                        refetch()
                                    }}
                                    loading={isSearching}
                                    size={isMobile ? "lg" : "md"}
                                >
                                    <IconRefresh size={isMobile ? 20 : 16} />
                                </ActionIcon>
                            </Tooltip>
                        )}
                        <CloseButton onClick={onClose} size={isMobile ? "lg" : "sm"} />
                    </Group>
                </Box>

                {/* Search Results */}
                <Box
                    p={isMobile ? "lg" : "md"}
                    style={{
                        flex: 1,
                        overflowY: 'auto',
                        minHeight: 0,
                        maxHeight: isMobile ? 'calc(100vh - 80px)' : 'calc(80vh - 80px)' // Account for header height
                    }}
                >
                    {!searchQuery && (
                        <Center h={200}>
                            <Stack align="center" gap="xs">
                                <IconSearch size={isMobile ? 64 : 48} color="var(--mantine-color-gray-4)" />
                                <Text c="dimmed" size={isMobile ? "md" : "sm"}>
                                    {t('common.searchPlaceholder')}
                                </Text>
                            </Stack>
                        </Center>
                    )}

                    {searchQuery && searchQuery.length < 2 && (
                        <Center h={200}>
                            <Text c="dimmed" size={isMobile ? "md" : "sm"}>
                                {t('common.searchPlaceholder')}
                            </Text>
                        </Center>
                    )}

                    {isSearching && debouncedQuery.length >= 2 && (
                        <Stack gap="md">
                            <SimpleGrid cols={{ base: 2, sm: 2, md: 3 }} spacing={isMobile ? "lg" : "md"}>
                                {Array.from({ length: 6 }).map((_, index) => (
                                    <Skeleton key={index} height={isMobile ? 200 : 180} radius="md" />
                                ))}
                            </SimpleGrid>
                        </Stack>
                    )}

                    {error && (
                        <Center h={200}>
                            <Text c="red" size={isMobile ? "md" : "sm"}>
                                {t('common.searchError')}
                            </Text>
                        </Center>
                    )}

                    {searchResults && searchResults.length === 0 && !isSearching && (
                        <Center h={200}>
                            <Stack align="center" gap="xs">
                                <Text c="dimmed" size={isMobile ? "md" : "sm"}>
                                    {t('common.noSearchResults', { term: debouncedQuery })}
                                </Text>
                                <Text c="dimmed" size={isMobile ? "sm" : "xs"}>
                                    {t('common.searchAdjustTerms')}
                                </Text>
                            </Stack>
                        </Center>
                    )}

                    {searchResults && searchResults.length > 0 && (
                        <SimpleGrid cols={{ base: 2, sm: 2, md: 3 }} spacing={isMobile ? "lg" : "md"}>
                            {searchResults.map((gamePlatform: GamePlatform & { lowestPrice?: LowestPrice | null }) => (
                                <GameCard
                                    key={`${gamePlatform.gameId}-${gamePlatform.platformId}`}
                                    gamePlatform={gamePlatform}
                                    onClick={handleCardClick}
                                    showDescription={true}
                                    compact={true}
                                />
                            ))}
                        </SimpleGrid>
                    )}
                </Box>
            </Stack>
        </Modal>
    )
} 